/**
 * Strategic Word Count Service
 * Helper utilities for strategic word count analysis
 */

/**
 * Detect paragraphs in essay content
 * @param {string} essayContent - The essay text
 * @returns {Array} Array of paragraph objects
 */
export function detectParagraphs(essayContent) {
  if (!essayContent || typeof essayContent !== 'string') {
    return [];
  }

  // Split by double newlines (standard paragraph break)
  const paragraphs = essayContent
    .split(/\n\n+/)
    .map(p => p.trim())
    .filter(p => p.length > 0);

  return paragraphs.map((text, index) => ({
    id: index + 1,
    text,
    wordCount: calculateWordCount(text),
    startIndex: essayContent.indexOf(text),
    endIndex: essayContent.indexOf(text) + text.length
  }));
}

/**
 * Calculate precise word count
 * @param {string} text - The text to count
 * @returns {number} Word count
 */
export function calculateWordCount(text) {
  if (!text || typeof text !== 'string') {
    return 0;
  }

  return text
    .trim()
    .replace(/\s+/g, ' ') // Normalize whitespace
    .split(' ')
    .filter(word => word.length > 0)
    .length;
}

/**
 * Determine mode based on current and target word counts
 * @param {number} currentCount - Current word count
 * @param {number} targetCount - Target word count
 * @returns {string} 'reduce' or 'expand'
 */
export function determineMode(currentCount, targetCount) {
  return currentCount > targetCount ? 'reduce' : 'expand';
}

/**
 * Validate that change threshold meets minimum for strategic editing
 * @param {number} currentCount - Current word count
 * @param {number} targetCount - Target word count
 * @param {number} minimumThreshold - Minimum word difference (default 50)
 * @returns {Object} Validation result
 */
export function validateChangeThreshold(currentCount, targetCount, minimumThreshold = 50) {
  const changeNeeded = Math.abs(targetCount - currentCount);
  const isValid = changeNeeded >= minimumThreshold;

  return {
    isValid,
    changeNeeded,
    percentageChange: ((changeNeeded / currentCount) * 100).toFixed(0),
    message: isValid
      ? `Strategic editing appropriate: ${changeNeeded} words needed`
      : `Change too small: ${changeNeeded} words (minimum ${minimumThreshold} required)`
  };
}

/**
 * Format analysis response for UI display
 * @param {Object} apiResponse - Response from Claude API
 * @returns {Object} Formatted analysis
 */
export function formatAnalysisForDisplay(apiResponse) {
  if (!apiResponse || !apiResponse.sections) {
    return null;
  }

  return {
    currentCount: apiResponse.currentCount,
    targetCount: apiResponse.targetCount,
    mode: apiResponse.mode,
    sections: apiResponse.sections.map(section => ({
      ...section,
      // Add UI-specific properties
      statusColor: getStatusColor(section.status, apiResponse.mode),
      priorityLabel: getPriorityLabel(section.priority),
      impactText: formatImpact(section.impact)
    })),
    summary: {
      ...apiResponse.summary,
      estimatedResult: apiResponse.mode === 'reduce'
        ? apiResponse.summary.projectedCount
        : apiResponse.summary.projectedCount
    },
    strategicGuidance: apiResponse.strategicGuidance,
    timestamp: apiResponse.timestamp
  };
}

/**
 * Get status color for UI
 * @param {string} status - Section status
 * @param {string} mode - Analysis mode
 * @returns {string} Tailwind color class
 */
export function getStatusColor(status, mode) {
  if (mode === 'reduce') {
    switch (status) {
      case 'essential':
      case 'good':
        return 'teal';
      case 'excessive':
      case 'redundant':
      case 'unnecessary':
      case 'tangent':
        return 'coral';
      default:
        return 'stone';
    }
  } else {
    // expand mode
    switch (status) {
      case 'essential':
      case 'good':
        return 'stone';
      case 'underdeveloped':
      case 'rushed':
      case 'thin':
        return 'teal';
      default:
        return 'stone';
    }
  }
}

/**
 * Get priority label for display
 * @param {string} priority - Section priority
 * @returns {string} Display label
 */
export function getPriorityLabel(priority) {
  const labels = {
    'keep': 'Keep',
    'cut-major': 'Cut Entirely',
    'cut-moderate': 'Trim Significantly',
    'expand': 'Expand'
  };

  return labels[priority] || priority;
}

/**
 * Format impact number for display
 * @param {number} impact - Word impact (negative for cuts, positive for additions)
 * @returns {string} Formatted text
 */
export function formatImpact(impact) {
  if (!impact || impact === 0) {
    return '';
  }

  const absImpact = Math.abs(impact);
  const prefix = impact > 0 ? '+' : '';

  return `${prefix}${impact} words`;
}

/**
 * Calculate total potential impact from sections
 * @param {Array} sections - Section array
 * @returns {number} Total impact
 */
export function calculateTotalImpact(sections) {
  if (!sections || !Array.isArray(sections)) {
    return 0;
  }

  return sections.reduce((total, section) => {
    return total + (section.impact || 0);
  }, 0);
}

/**
 * Group sections by priority for display
 * @param {Array} sections - Section array
 * @returns {Object} Sections grouped by priority
 */
export function groupSectionsByPriority(sections) {
  if (!sections || !Array.isArray(sections)) {
    return {};
  }

  return sections.reduce((groups, section) => {
    const priority = section.priority;
    if (!groups[priority]) {
      groups[priority] = [];
    }
    groups[priority].push(section);
    return groups;
  }, {});
}

/**
 * Validate essay content before sending to API
 * @param {string} essayContent - The essay text
 * @returns {Object} Validation result
 */
export function validateEssayContent(essayContent) {
  if (!essayContent || typeof essayContent !== 'string') {
    return {
      isValid: false,
      error: 'Essay content is required'
    };
  }

  const trimmed = essayContent.trim();

  if (trimmed.length < 100) {
    return {
      isValid: false,
      error: 'Essay is too short for strategic analysis (minimum 100 characters)'
    };
  }

  const wordCount = calculateWordCount(trimmed);

  if (wordCount < 200) {
    return {
      isValid: false,
      error: 'Essay is too short for strategic analysis (minimum 200 words)'
    };
  }

  return {
    isValid: true,
    wordCount
  };
}

/**
 * Save strategic analysis to database
 * @param {Object} supabase - Supabase client
 * @param {string} essayId - Essay ID
 * @param {Object} analysisData - Analysis data to save
 * @returns {Promise<Object>} Save result
 */
export async function saveStrategicAnalysis(supabase, essayId, analysisData) {
  try {
    // Get existing strategic analyses
    const { data: essay, error: fetchError } = await supabase
      .from('college_journey_essays')
      .select('strategic_word_count_analysis')
      .eq('id', essayId)
      .single();

    if (fetchError) {
      console.error('Error fetching essay:', fetchError);
      throw fetchError;
    }

    // Create new analysis session
    const newSession = {
      mode: analysisData.mode,
      currentCount: analysisData.currentCount,
      targetCount: analysisData.targetCount,
      sections: analysisData.sections,
      summary: analysisData.summary,
      strategicGuidance: analysisData.strategicGuidance,
      timestamp: analysisData.timestamp || new Date().toISOString()
    };

    // Append to existing sessions or create new array
    const existingSessions = essay.strategic_word_count_analysis || [];
    const updatedSessions = [...existingSessions, newSession];

    // Update essay record
    const { error: updateError } = await supabase
      .from('college_journey_essays')
      .update({
        strategic_word_count_analysis: updatedSessions,
        updated_at: new Date().toISOString()
      })
      .eq('id', essayId);

    if (updateError) {
      console.error('Error saving strategic analysis:', updateError);
      throw updateError;
    }

    return {
      success: true,
      sessionsCount: updatedSessions.length
    };

  } catch (error) {
    console.error('Error in saveStrategicAnalysis:', error);
    return {
      success: false,
      error: error.message
    };
  }
}

/**
 * Get latest strategic analysis for an essay
 * @param {Object} supabase - Supabase client
 * @param {string} essayId - Essay ID
 * @returns {Promise<Object>} Latest analysis or null
 */
export async function getLatestStrategicAnalysis(supabase, essayId) {
  try {
    const { data: essay, error } = await supabase
      .from('college_journey_essays')
      .select('strategic_word_count_analysis')
      .eq('id', essayId)
      .single();

    if (error) {
      console.error('Error fetching strategic analysis:', error);
      return null;
    }

    const sessions = essay.strategic_word_count_analysis;

    if (!sessions || sessions.length === 0) {
      return null;
    }

    // Return most recent session
    return sessions[sessions.length - 1];

  } catch (error) {
    console.error('Error in getLatestStrategicAnalysis:', error);
    return null;
  }
}

/**
 * Get all strategic analyses for an essay
 * @param {Object} supabase - Supabase client
 * @param {string} essayId - Essay ID
 * @returns {Promise<Array>} All analysis sessions
 */
export async function getAllStrategicAnalyses(supabase, essayId) {
  try {
    const { data: essay, error } = await supabase
      .from('college_journey_essays')
      .select('strategic_word_count_analysis')
      .eq('id', essayId)
      .single();

    if (error) {
      console.error('Error fetching strategic analyses:', error);
      return [];
    }

    return essay.strategic_word_count_analysis || [];

  } catch (error) {
    console.error('Error in getAllStrategicAnalyses:', error);
    return [];
  }
}
